import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";

import type {
  SpecLineLocationNotesBrowserInput,
  SpecLineLocationsBrowserInput,
  SpecLineLocationTypeBrowserInput,
  SpecLocationCreateBrowserInput,
  SpecLocationDeleteBrowserInput,
  SpecLocationRenameBrowserInput,
} from "@/api/contracts/spec-editor";
import {
  specLineUpdateLocationNotes,
  specLineUpdateLocations,
  specLineUpdateLocationType,
  specLocationCreate,
  specLocationDelete,
  specLocationRename,
} from "@/api/functions/spec-editor";
import {
  editorMutationOptions,
  patchLine,
  patchRoom,
  type EditorGuess,
} from "@/hooks/spec-editor-cache";
import type { SpecLinesLocation } from "@/hooks/specifications";

// The Editor's location verbs (#802): what #808's locations select and notes
// table call. Each runs through #797's harness (`editorMutationOptions`) with
// its own `ERRORS` key, and its result — the line or lines whose rooms
// changed, whole, and for the four that change the room list or a line's
// rooms the version's rooms — lands through the one cache updater (ADR-0014
// decisions 1–6). The session's room list (#965 N12) changes only by each
// verb's own room, as the source's `arrayUpdates` did: Add appends the room it
// ticks, a delete drops its room, a rename names its room in place, and the
// checklist leaves the list alone — so another tab's rooms show only after a
// reload.
//
// **Two verbs guess** (the selections rulings item 11): the inline rename,
// which renames the room in the list and on every held placement of it, and
// the location-type radio — each at once, rolled back exactly on failure, and
// overwritten by the result on success. The checklist, Add Location, the
// delete and a room's note wait for the server.
//
// Each hook's variables are the verb's browser input, which is also its cache
// target. No hook touches the Active selection: no location verb changes which
// selection is active (#811 owns it).

/** The checklist — the line's rooms by id; kept rooms keep their notes. */
export function updateSpecLineLocationsMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineLocationsBrowserInput>(queryClient, {
    mutationFn: (data) => specLineUpdateLocations({ data }),
    error: "UPDATE_LINE_LOCATIONS",
  });
}

export function useUpdateSpecLineLocations() {
  return useMutation(updateSpecLineLocationsMutationOptions(useQueryClient()));
}

/** Add Location's answer: the shared result and the room it ticked. */
export type SpecLocationCreateResult = Awaited<ReturnType<typeof specLocationCreate>>;

/** Add Location's change to the session's room list (#965 N12): the room it
 *  created or linked, appended unless held — Falcon's `arrayUpdates add` of
 *  that one id (`locations.py:131-140`, `objectCache.ts:86-87`), which the
 *  answer names, so a room another tab put on the line stays out until a
 *  reload. */
export const createSpecLocationRooms = (
  rooms: SpecLinesLocation[],
  { locationId, locations }: SpecLocationCreateResult,
): SpecLinesLocation[] => {
  const added = locations?.find(({ id }) => id === locationId);
  return added && !rooms.some(({ id }) => id === added.id) ? [...rooms, added] : rooms;
};

/** Add Location — the room created (or the same-named one linked) and ticked
 *  for the line; the result ticks it, so no follow-up call is needed. */
export function createSpecLocationMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLocationCreateBrowserInput, SpecLocationCreateResult>(
    queryClient,
    {
      mutationFn: (data) => specLocationCreate({ data }),
      error: "CREATE_SPECIFIC_LOCATION",
      settleRooms: createSpecLocationRooms,
    },
  );
}

export function useCreateSpecLocation() {
  return useMutation(createSpecLocationMutationOptions(useQueryClient()));
}

/** Delete a room — from every line of the version, its notes with it. */
export function deleteSpecLocationMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLocationDeleteBrowserInput>(queryClient, {
    mutationFn: (data) => specLocationDelete({ data }),
    error: "DELETE_SPECIFIC_LOCATION",
    // The deleted room leaves the session's list, and nothing else does
    // (`objectCache.ts:88-89`).
    settleRooms: (rooms, _result, { locationId }) => rooms.filter(({ id }) => id !== locationId),
  });
}

export function useDeleteSpecLocation() {
  return useMutation(deleteSpecLocationMutationOptions(useQueryClient()));
}

/** A room's note on the line — `Add a note`, `Edit`, `Clear Note`. */
export function updateSpecLineLocationNotesMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineLocationNotesBrowserInput>(queryClient, {
    mutationFn: (data) => specLineUpdateLocationNotes({ data }),
    error: "UPDATE_LINE_LOCATION_NOTE",
  });
}

export function useUpdateSpecLineLocationNotes() {
  return useMutation(updateSpecLineLocationNotesMutationOptions(useQueryClient()));
}

/** The rename's guess: the room renamed in place, in the list and on every
 *  held placement — the name as typed; the server's trimmed name wins. */
export const renameSpecLocationGuess: EditorGuess<SpecLocationRenameBrowserInput> = (
  data,
  { locationId, name },
) => patchRoom(data, locationId, name);

/** The rename's guess into the session's room list (#965 N12): the room
 *  renamed where it sits — the source's `specificLocations[id].name`
 *  (`LineLocations/actions.ts:127-133`). */
export const renameSpecLocationRoomsGuess = (
  rooms: SpecLinesLocation[],
  { locationId, name }: SpecLocationRenameBrowserInput,
): SpecLinesLocation[] => rooms.map((room) => (room.id === locationId ? { ...room, name } : room));

/** Rename a room — optimistic; every line placed in it comes back. */
export function renameSpecLocationMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLocationRenameBrowserInput>(queryClient, {
    mutationFn: (data) => specLocationRename({ data }),
    error: "EDIT_SPECIFIC_LOCATION_NAME",
    guess: renameSpecLocationGuess,
    guessRooms: renameSpecLocationRoomsGuess,
    // The answer's name for this room, where it sits; the rest of the list as
    // held (`locations.py:94-101`).
    settleRooms: (rooms, { locations }, { locationId }) => {
      const renamed = locations?.find(({ id }) => id === locationId);
      return renamed ? rooms.map((room) => (room.id === locationId ? renamed : room)) : rooms;
    },
  });
}

export function useRenameSpecLocation() {
  return useMutation(renameSpecLocationMutationOptions(useQueryClient()));
}

/** The type radio's guess: the held line's type, its rooms kept. */
export const updateSpecLineLocationTypeGuess: EditorGuess<SpecLineLocationTypeBrowserInput> = (
  data,
  { lineId, locationType },
) => patchLine(data, lineId, (line) => ({ ...line, locationType }));

/** The location-type radio — optimistic; the response wins. */
export function updateSpecLineLocationTypeMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineLocationTypeBrowserInput>(queryClient, {
    mutationFn: (data) => specLineUpdateLocationType({ data }),
    error: "UPDATE_LINE_LOCATION_TYPE",
    guess: updateSpecLineLocationTypeGuess,
  });
}

export function useUpdateSpecLineLocationType() {
  return useMutation(updateSpecLineLocationTypeMutationOptions(useQueryClient()));
}
